import React, { useState } from 'react';
import { styled } from '@compiled/react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Avatar, { type SizeType } from '@atlaskit/avatar';
import { LoadingButton } from '@atlaskit/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';

import { token } from '@atlaskit/tokens';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useFlagService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	InsightIcon,
	type InsightIconProps,
} from '@atlassian/jira-servicedesk-insight-icon/src/ui/index.tsx';
import {
	useObjectStoreState,
	useObjectActions,
	useObjectTypeId,
} from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/store/index.tsx';
import type { CmdbAvatar as InsightAvatar } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/cmdb-object.tsx';
import { toCmdbObjectTypeId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import { FileBrowser } from '@atlassian/jira-servicedesk-insight-shared-ui/src/ui/file-browser/index.tsx';
import messages from './messages.tsx';

type TestProps = {
	initialIsOpen?: boolean;
};

export type Props = {
	canEdit: boolean;
	avatar: InsightAvatar;
} & TestProps;

export const IconEditDialog = (props: Props) => {
	const { canEdit, avatar, initialIsOpen } = props;
	const [isOpen, setIsOpen] = useState<boolean>(false);

	const [{ objectDetails }, { deleteObjectAvatar }] = useObjectStoreState();
	const [, { updateObjectAvatar }] = useObjectActions();
	const [objectTypeId] = useObjectTypeId();
	const [loading, setLoading] = useState<boolean>(false);

	const { formatMessage } = useIntl();
	const hasAvatar = Boolean(objectDetails?.hasAvatar);
	const { showFlag } = useFlagService();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const avatarIconProps: InsightIconProps = {
		mediaClientConfig: avatar.mediaClientConfig,
		iconUrl: avatar.url72,
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		size: 'large' as SizeType,
		label: '',
	};

	const iconUrl = `${avatarIconProps.iconUrl}${
		hasAvatar && avatar?.avatarUUID ? `?avatarUUID=${avatar.avatarUUID}` : ''
	}`;

	const handleAddAvatar = () => {
		setIsOpen(true);
		fireUIAnalytics(createAnalyticsEvent({}), 'insightObjectEditObjectIcon clicked');
	};

	const onImagePicked = (files: File[]) => {
		fireUIAnalytics(
			createAnalyticsEvent({}),
			'button clicked',
			'insightObjectEditObjectIconConfirmUpload',
		);
		setLoading(true);
		updateObjectAvatar({
			selectedImageFile: files[0],
			onError: () => {
				setLoading(false);
				showFlag({
					type: 'error',
					title: messages.genericErrorMessageTitle,
					description: messages.genericErrorMessageDescription,
					isAutoDismiss: true,
				});
			},
			onSuccess: () => {
				setIsOpen(false);
				setLoading(false);
				showFlag({
					type: 'success',
					title: messages.successFlagTitle,
					isAutoDismiss: true,
				});
			},
			objectTypeId: toCmdbObjectTypeId(objectTypeId),
		});

		return undefined;
	};

	const handleDeleteAvatar = () => {
		fireUIAnalytics(createAnalyticsEvent({}), 'insightObjectRemoveObjectIcon clicked');
		setLoading(true);
		deleteObjectAvatar({
			onSuccess: () => {
				setLoading(false);
				showFlag({
					type: 'success',
					title: messages.removeSuccessFlagTitle,
					isAutoDismiss: true,
				});
			},
			onFailure: () => {
				setLoading(false);
				showFlag({
					type: 'error',
					title: messages.removeFailFlagTitle,
					description: messages.removeFailFlagDescription,
					isAutoDismiss: true,
				});
			},
		});
	};

	if (canEdit === false) {
		return <InsightIcon {...avatarIconProps} />;
	}

	return (
		<>
			{loading ? (
				<DummyLoadingAvatar appearance="primary" isDisabled isLoading />
			) : (
				<DropdownMenu
					trigger={({ triggerRef, ...triggerProps }) => (
						<Avatar
							{...triggerProps}
							testId="servicedesk-insight-object-view.ui.header.icon-edit-dialog.object-avatar"
							appearance="square"
							size={avatarIconProps.size}
							src={iconUrl}
							ref={triggerRef}
							label={formatMessage(messages.avatarLabel)}
						/>
					)}
					isOpen={initialIsOpen}
				>
					<DropdownItemGroup>
						<DropdownItem
							onClick={handleAddAvatar}
							testId="servicedesk-insight-object-view.ui.header.icon-edit-dialog.add-avatar"
						>
							{formatMessage(hasAvatar ? messages.editAvatar : messages.addAvatar)}
						</DropdownItem>
						<DropdownItem
							isDisabled={!hasAvatar}
							onClick={handleDeleteAvatar}
							testId="servicedesk-insight-object-view.ui.header.icon-edit-dialog.remove-avatar"
						>
							{formatMessage(messages.removeAvatar)}
						</DropdownItem>
					</DropdownItemGroup>
				</DropdownMenu>
			)}
			<FileBrowser
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				onReceiveFiles={onImagePicked}
				accept="image/*"
				multiple={false}
				testId="servicedesk-insight-object-view.ui.header.icon-edit-dialog.object-avatar-input"
			/>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DummyLoadingAvatar = styled(LoadingButton)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&&': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		height: `${gridSize * 5}px`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		width: `${gridSize * 5}px`,
		marginTop: token('space.025', '2px'),
		marginRight: token('space.025', '2px'),
		marginBottom: token('space.025', '2px'),
		marginLeft: token('space.025', '2px'),
	},
});
